import { Link, Badge, Icon } from "@oysterhr/ui"
import { type BadgeStateItem } from "~/shared/components/StatesDescription"
import { Termination, Company, TerminationPaidInFullStatusType } from "@oysterhr/schema"
import { useTranslation } from "react-i18next"
import { staffInvoicesPath } from "@oysterhr/invoicing"
import Section from "~/adminApp/pages/EmploymentPage/TabShared/Section"
import { idToStrongId } from "@oysterhr/common"

interface DisplayProps {
  termination: Termination
  company: Company
  lastDayOn: string
  refetch: () => void
}

interface StatusFieldProps {
  state: TerminationPaidInFullStatusType
}

const PaidInFullStateLabelsMap: Record<TerminationPaidInFullStatusType, BadgeStateItem> = {
  [TerminationPaidInFullStatusType.NotStarted]: {
    variation: "gray",
    translationKey: "paidInFullStatus.notStarted",
    descriptionKey: "paidInFullStatusDescription.notStarted",
  },
  [TerminationPaidInFullStatusType.PendingPayment]: {
    variation: "warning",
    translationKey: "paidInFullStatus.pendingPayment",
    descriptionKey: "paidInFullStatusDescription.pendingPayment",
  },
  [TerminationPaidInFullStatusType.PaidInFull]: {
    variation: "success",
    translationKey: "paidInFullStatus.paidInFull",
    descriptionKey: "paidInFullStatusDescription.paidInFull",
  },
}

function StatusField({ state }: StatusFieldProps) {
  const { t } = useTranslation("staff_interface/TabTermination")
  const { variation, translationKey } = PaidInFullStateLabelsMap[state]

  return (
    <div>
      {t("offboardingInvoicePaymentsTitle")}
      &nbsp;
      <Badge variation={variation}>{t(translationKey)}</Badge>
    </div>
  )
}

function InvoicePaymentsDisplay({ termination, company }: DisplayProps) {
  const { t } = useTranslation("staff_interface/TabTermination")
  const invoicesUrl = (company: Company) => {
    const companyId = idToStrongId(company.id)
    const url = staffInvoicesPath(`?companyId=${companyId}`)

    return url
  }

  return (
    <Section
      title={StatusField({ state: termination.paidInFullStatus })}
      dataTestId="invoice-payments-display"
    >
      {termination.paidInFullStatus === TerminationPaidInFullStatusType.NotStarted ? (
        <div className="text-xs">{t("noInvoices")}</div>
      ) : (
        <div className="text-sm">
          <div className="col-span-2 mt-4">
            <Link
              as="a"
              href={invoicesUrl(company)}
              className="text-sm"
              startAdornment={<Icon name="ArrowSquareOut" />}
            >
              {t("linkToInvoices")}
            </Link>
          </div>
        </div>
      )}
    </Section>
  )
}

export default InvoicePaymentsDisplay
